import React, { useState, useEffect, useContext } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerShadowPng from "leaflet/dist/images/marker-shadow.png";
import { Box, Skeleton, ToggleButtonGroup, ToggleButton } from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import api from "../../utils/api";
import { ThemeContext } from "../../utils/ThemeContext";

// Same marker icon setup as Locations.js on the dashboard
const MARKER_ICON = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
  shadowUrl: markerShadowPng,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  shadowAnchor: [12, 41],
});

const TILES = {
  street: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  streetDark: {
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>',
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri &mdash; Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
  },
};

/**
 * MiniMap. compact embedded map using react-leaflet + OpenStreetMap / ESRI Satellite.
 * Fetches pre-cached lat/lng from /member-location/:clubMemberCode
 */
const MiniMap = ({ clubMemberCode, height = 160 }) => {
  const { isDark } = useContext(ThemeContext);
  const [locationData, setLocationData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [viewMode, setViewMode] = useState("street"); // "street" | "satellite"

  useEffect(() => {
    if (!clubMemberCode) return;
    setLocationData(null);
    setFailed(false);

    api
      .get(`/member-location/${encodeURIComponent(clubMemberCode)}`)
      .then((res) => {
        const lat = parseFloat(res.data?.lat);
        const lng = parseFloat(res.data?.lng);
        const address = res.data?.address || "";
        if (!isNaN(lat) && !isNaN(lng)) {
          setLocationData({ lat, lng, address });
        } else {
          setFailed(true);
        }
      })
      .catch((err) => {
        if (err?.response?.status !== 404) {
          console.error("MiniMap location error:", err);
        }
        setFailed(true);
      });
  }, [clubMemberCode]);

  if (!clubMemberCode || failed) return null;

  if (!locationData) {
    return (
      <Skeleton
        variant="rectangular"
        height={height}
        sx={{ borderRadius: "12px", mt: 1.5 }}
      />
    );
  }

  const activeTile =
    viewMode === "satellite"
      ? TILES.satellite
      : isDark
      ? TILES.streetDark
      : TILES.street;

  const borderColor = isDark
    ? "rgba(255,255,255,0.08)"
    : "rgba(47,99,173,0.12)";

  return (
    <Box
      sx={{
        mt: 1.5,
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${borderColor}`,
        height: `${height}px`,
        minHeight: `${height}px`,
        position: "relative",
        "& .leaflet-container": {
          borderRadius: "12px",
          fontFamily: "inherit",
        },
        "& .leaflet-popup-content-wrapper": {
          borderRadius: "10px",
          boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
        },
        "& .leaflet-popup-content": {
          margin: "8px 10px",
          fontSize: "0.85rem",
          color: "#223142",
        },
      }}
    >
      <MapContainer
        key={`${locationData.lat}-${locationData.lng}`}
        center={[locationData.lat, locationData.lng]}
        zoom={15}
        scrollWheelZoom={false}
        dragging={false}
        zoomControl={false}
        doubleClickZoom={false}
        attributionControl={false}
        style={{ height: `${height}px`, width: "100%" }}
      >
        <TileLayer
          key={viewMode + String(isDark)}
          url={activeTile.url}
          attribution={activeTile.attribution}
        />
        <Marker position={[locationData.lat, locationData.lng]} icon={MARKER_ICON}>
          <Popup>{locationData.address}</Popup>
        </Marker>
      </MapContainer>

      {/* View toggle. floats over the top-right corner of the map */}
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 1000,
        }}
      >
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_e, val) => { if (val) setViewMode(val); }}
          size="small"
          sx={{
            backgroundColor: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(4px)",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
            "& .MuiToggleButton-root": {
              border: "none",
              px: 1,
              py: 0.4,
              minWidth: 32,
              color: "#5f6b7a",
              "&.Mui-selected": {
                backgroundColor: "#2f63ad",
                color: "#fff",
                "&:hover": { backgroundColor: "#1f4f94" },
              },
            },
          }}
        >
          <ToggleButton value="street" title="Street view">
            <MapIcon sx={{ fontSize: 15 }} />
          </ToggleButton>
          <ToggleButton value="satellite" title="Satellite view">
            <SatelliteAltIcon sx={{ fontSize: 15 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};

export default MiniMap;
